import { debounce } from 'lodash/function';

const defaults = {
  initialPanel: false,
  triggerClass: 'accordion__trigger',
  tabsClass: 'accordion__tabs',
  panelClass: 'accordion__toggle',
  breakPoint: 992
};

class Accordion {

  constructor (element, settings) {
    this.accordion = element;
    this.options = Object.assign({}, defaults, settings);

    this.accordionTriggers = element.getElementsByClassName(this.options.triggerClass);
    this.accordionPanels = element.getElementsByClassName(this.options.panelClass);
    this.accordionTabs = element.getElementsByClassName(this.options.tabsClass)[0];

    this.multiSelect = element.hasAttribute('aria-multiselectable');
    this.hasTabs = element.classList.contains('accordion--tabs');
    this.isTabMode = false;

    this.totalPanels = this.accordionTriggers.length || 0;
    this.maxIndex = this.totalPanels - 1;
    this.openIndexValue = element.getAttribute('data-initial-panel') || this.options.initialPanel;
    this.openIndex = this.openIndexValue !== false ? parseInt(this.openIndexValue) : false;

    this.clickListener = this.clickEvent.bind(this);
    this.keydownListener = this.keydownEvent.bind(this);
    this.resizeListener = this.resizeEvent.bind(this);
    this.debouncedResize = debounce(this.resizeListener, 75, {
      leading: true,
      trailing: true
    });

    this.keyCodes = {
      left: 37,
      right: 39,
      prev: 38,
      next: 40,
      enter: 13,
      space: 32,
      home: 36,
      end: 35
    };
  }

  addIndexesToTriggers () {
    [...this.accordionTriggers].forEach((item, index) => {
      item.triggerIndex = index;
    });
  }

  removeIndexesFromTriggers () {
    [...this.accordionTriggers].forEach((item, index) => {
      delete item.triggerIndex;
      delete item.isSelected;
      delete item.isOpen;
    });
  }

  closeItem (index) {
    this.deselectTrigger(index);
    this.contractItem(index);
  }

  openItem (index) {
    if (!this.multiSelect) {
      this.openIndex = index;
    }
    this.selectTrigger(index);
    this.expandItem(index);
  }

  addEventListeners () {
    [...this.accordionTriggers].forEach(item => {
      item.addEventListener('click', this.clickListener, false);
      item.addEventListener('keydown', this.keydownListener, false);
    });

    if (this.hasTabs === true) {
      window.addEventListener('resize', this.debouncedResize, false);
    }
  }

  removeEventListeners () {
    [...this.accordionTriggers].forEach(item => {
      item.removeEventListener('click', this.clickListener, false);
      item.removeEventListener('keydown', this.keydownListener, false);
    });

    if (this.hasTabs === true) {
      window.removeEventListener('resize', this.debouncedResize, false);
    }
  }

  focusNewTrigger (index) {
    this.deselectAllTriggers();
    this.selectTrigger(index);
    this.focusTrigger(index);
  }

  clickEvent (e) {
    e.preventDefault();
    this.toggleEvent(e.currentTarget);
  }

  resizeEvent () {
    if (document.body.clientWidth <= this.options.breakPoint && this.isTabMode === true) {
      [...this.accordionTriggers].forEach((item, count) => {
        const panel = this.accordionPanels[count];
        panel.parentNode.insertBefore(item, panel);
      });

      this.isTabMode = false;
    } else if (document.body.clientWidth > this.options.breakPoint && this.isTabMode === false) {
      [...this.accordionTriggers].forEach(item => {
        this.accordionTabs.appendChild(item);
      });

      this.isTabMode = true;

      if (this.openIndex === false) {
        this.openItem(0);
      }
    }
  }

  keydownEvent (e) {
    const { triggerIndex } = e.currentTarget;

    switch (e.keyCode) {
      case this.keyCodes.home:
        e.preventDefault();
        this.focusNewTrigger(0);
        break;
      case this.keyCodes.end:
        e.preventDefault();
        this.focusNewTrigger(this.maxIndex);
        break;
      case this.keyCodes.space:
      case this.keyCodes.enter:
        e.preventDefault();
        this.toggleEvent(e.currentTarget);
        break;
      case this.keyCodes.left:
      case this.keyCodes.prev:
        e.preventDefault();
        if (triggerIndex > 0) {
          this.focusNewTrigger(triggerIndex - 1);
        }
        break;
      case this.keyCodes.right:
      case this.keyCodes.next:
        e.preventDefault();
        if (triggerIndex < (this.maxIndex)) {
          this.focusNewTrigger(triggerIndex + 1);
        }
        break;
      default:
        return;
    }
  }

  toggleEvent (target) {
    const { isOpen, triggerIndex } = target;
    const isToggling = !this.multiSelect && this.openIndex === triggerIndex;

    if (isToggling) {
      this.toggle(target);
    } else if (isOpen) {
      this.close(target);
    } else {
      this.open(target);
    }
  }

  allowTriggerFocus (index = 0) {
    const selectedTrigger = this.accordionTriggers[index];
    selectedTrigger.setAttribute('tabindex', 0);
  }

  focusTrigger (index = 0) {
    this.accordionTriggers[index].focus();
  }

  deselectAllTriggers () {
    [...this.accordionTriggers].forEach((item, index) => this.deselectTrigger(index));
  }

  selectTrigger (index = 0) {
    const selectedTrigger = this.accordionTriggers[index];
    selectedTrigger.isSelected = true;
    selectedTrigger.classList.add('is-selected');
    selectedTrigger.setAttribute('aria-selected', true);
    selectedTrigger.setAttribute('tabindex', 0);
  }

  deselectTrigger (index = 0) {
    const selectedTrigger = this.accordionTriggers[index];
    selectedTrigger.isSelected = false;
    selectedTrigger.classList.remove('is-selected');
    selectedTrigger.setAttribute('aria-selected', false);
    selectedTrigger.setAttribute('tabindex', -1);
  }

  expandItem (index = 0) {
    const selectedTrigger = this.accordionTriggers[index];
    selectedTrigger.isOpen = true;
    selectedTrigger.setAttribute('aria-expanded', true);
    selectedTrigger.classList.add('is-expanded');

    const selectedPanel = this.accordionPanels[index];
    selectedPanel.setAttribute('aria-hidden', false);
    selectedPanel.classList.remove('is-hidden');
  }

  contractItem (index = 0) {
    const selectedTrigger = this.accordionTriggers[index];
    selectedTrigger.isOpen = false;
    selectedTrigger.setAttribute('aria-expanded', false);
    selectedTrigger.classList.remove('is-expanded');

    const selectedPanel = this.accordionPanels[index];
    selectedPanel.setAttribute('aria-hidden', true);
    selectedPanel.classList.add('is-hidden');
  }

  removeAllAttrs () {
    [...this.accordionTriggers].forEach(item => {
      item.removeAttribute('aria-expanded');
      item.removeAttribute('aria-selected');
      item.classList.remove('is-expanded');
    });

    [...this.accordionPanels].forEach(item => {
      item.removeAttribute('aria-hidden');
      item.removeAttribute('tabindex');
      item.classList.remove('is-hidden');
    });
  }

  closeAll () {
    [...this.accordionTriggers].forEach((item, index) => this.closeItem(index));
  }

  open (target) {
    const { triggerIndex } = target;

    if (this.openIndex !== false && !this.multiSelect) {
      this.closeItem(this.openIndex);
    }
    this.deselectAllTriggers();
    this.openItem(triggerIndex);
  }

  close (target) {
    const { triggerIndex } = target;

    if (this.multiSelect) {
      this.closeItem(triggerIndex);
      this.selectTrigger(triggerIndex);
    } else {
      this.closeItem(triggerIndex);
    }
  }

  toggle (target) {
    const { isOpen, triggerIndex } = target;

    if (isOpen && this.isTabMode !== true) {
      this.closeItem(this.openIndex);
      this.openIndex = false;
      this.selectTrigger(triggerIndex);
    } else {
      this.openItem(triggerIndex);
    }
  }

  init () {
    this.accordion.classList.add('is-initialized');
    this.addIndexesToTriggers();
    this.addEventListeners();
    this.closeAll();

    if (this.hasTabs === true) {
      this.resizeEvent();
    }

    if (this.openIndex !== false) {
      this.openItem(this.openIndex);
    }
  }

  destroy () {
    this.accordion.classList.remove('is-initialized');
    this.removeAllAttrs();
    this.removeEventListeners();
    this.removeIndexesFromTriggers();
  }
}

export default Accordion;
